import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const AddSuccessorDialog = ({
  open,
  onClose,
  allEmployees,
  allJobs,
  onSave,
  loading,
  currentJob,
  successorEmployees,
}) => {
  const { t } = useTranslation();

  // 1. Renamed state variables as requested
  const [successorEmployeeId, setSuccessorEmployeeId] = useState(null);
  const [successorJobId, setSuccessorJobId] = useState(null);

  useEffect(() => {
    if (open) {
      setSuccessorEmployeeId(null);
      // setSuccessorJobId(null);
      setSuccessorJobId(currentJob?.jobId || null);
    }
  }, [open]);

  // 3. handleClose specifically resets these state variables
  const handleClose = () => {
    setSuccessorEmployeeId(null);
    onClose();
  };

  const handleSaveSuccessor = () => {
    onSave(successorEmployeeId, successorJobId);
  };

  // Remove the employees that are already put as successors
  const filteredEmployees = useMemo(() => {
    return (allEmployees || []).filter(
      (emp) => !(successorEmployees || []).some((succ) => succ.employeeId === emp.uId)
    );
  }, [allEmployees, successorEmployees]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontWeight: "bold", textAlign: "center", fontSize: { xs: "1.2rem", sm: "1.6rem" } }}
      >
        {t("admin.successionPlanSteps.addSucc")}
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: "16px", sm: "20px 24px" },
          display: "grid",
          gap: 2,
        }}
      >
        <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 1 }}>
          {/* Field 1: Successor Employee */}

          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("admin.successionPlanSteps.successorJob")}
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {allJobs?.find((j) => j.jobId === successorJobId)?.jobName || "-"}
            </Typography>
          </Box>

          <Autocomplete
            options={filteredEmployees}
            getOptionLabel={(emp) => emp.name || ""}
            // value={allEmployees?.find((e) => e.uId === successorEmployeeId) || null}
            value={filteredEmployees.find((e) => e.uId === successorEmployeeId) || null}
            onChange={(_, newValue) => {
              setSuccessorEmployeeId(newValue?.uId || null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.successionPlanSteps.successorEmp") + " *"}
                fullWidth
              />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="secondary">
          {t("admin.employees.employeeDialog.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSaveSuccessor}
          color="primary"
          disabled={loading}
          sx={{
            "&:hover": { backgroundColor: "blue", color: "#fff" },
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="flex-end" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            t("common.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddSuccessorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  allEmployees: PropTypes.arrayOf(
    PropTypes.shape({
      uId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  allJobs: PropTypes.object,
  currentJob: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  successorEmployees: PropTypes.array,
};

export default AddSuccessorDialog;
